import WizardPagetemplate from '../../templates/WizardPagetemplate';
import { Box, Button, Grid, GridItem, LoadingDots } from '@flos/react-ui';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useSetQuoteApprovedTimestampHook } from '../../hooks/useSetQuoteApprovedTimestamp';
import ShoppingBasketGovernmentFee from '../ShoppingBasketPage/components/ShoppingBasketGovernmentFee';
import TotalPrice from '../../shared_components/TotalPrice/TotalPrice';
import CustomerBenefit from '../../shared_components/CustomerBenefit/CustomerBenefit';
import { jobAtom } from '../../recoil/atoms/jobAtom';
import { priceTaxAtom } from '../../recoil/atoms/priceTaxAtom';
import { paymentFrequencyMap, periodicityUnit } from '../../utils/currencyUtil';
import ShoppingBasketSummaryHeader from './components/ShoppingBasketSummaryHeader';
import { ACCEPTED } from '../../recoil/types/types';
import { sortedCoverablesListSelector } from '../../recoil/selectors/sortedCoverablesListSelector';
import ShoppingBasketSummaryCoverableList from './components/ShoppingBasketSummaryCoverableList';
import { useHippoText } from '../../hooks/useHippoText';
import { getCheckoutConfig } from '../CheckoutPage/configs/CheckoutAccordionConfig';
import { allCoverablesAtom } from '../../recoil/atoms/allCoverablesAtom';
import { externalCasesAtom } from '../../recoil/atoms/externalCasesAtom';
import { useAwaitBoundJob } from '../../hooks/useAwaitBoundJob';
import { useIssueInsurance } from '../../hooks/useIssueInsurance';
import type { WizardPageComponent } from '../../config/wizardPagesConfig';
import useWizardNavigation from '../../hooks/useWizardNavigation';
import { useState } from 'react';
import { jobInfoAtom } from '../../recoil/atoms/jobInfoAtom';
import { trackCheckout } from '../../utils/trackingUtil';
import { accountHolderAtom } from '../../recoil/atoms/accountHolderAtom';
import CallMeModal from '../../shared_components/CallMeModal/CallMeModal';
import type {
    PrivateContentDTO,
    YouthContentDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/content';
import { businessFlowSelector } from '../../recoil/selectors/getBusinessFlowSelector';
import { filterGovernmentFees } from '../ShoppingBasketPage/utils/ShoppingBasketUtil';
import { useAsyncRecoilValue } from '../../hooks/useAsyncRecoilValue';
import { insuranceCompaniesAtom } from '../../recoil/atoms/insuranceCompaniesAtom';
import { fetchedPaymentInformationAtom } from '../../recoil/atoms/fetchedPaymentInformationAtom';

const ShoppingBasketSummaryPage: WizardPageComponent = () => {
    const { wizardNextStep, wizardPreviousStep, wizardGotoStep, wizardStep } =
        useWizardNavigation();
    const [showModal, setShowModal] = useState(false);
    const [jobData, setJobData] = useRecoilState(jobAtom);
    const businessFlow = useRecoilValue(businessFlowSelector);
    const priceTaxData = useRecoilValue(priceTaxAtom);
    const { sortedCoverables } = useRecoilValue(sortedCoverablesListSelector);
    const hasCoverableWithChangedStatus = sortedCoverables.some(
        (coverable) => coverable.status === 'Changed'
    );
    const getHippoText = useHippoText();
    const selectedPaymentFrequency =
        jobData.selectedPaymentFrequency ?? paymentFrequencyMap.monthly;
    const isPaymentYearly = selectedPaymentFrequency === paymentFrequencyMap.annual;
    const paymentFrequencyPostfix = periodicityUnit[selectedPaymentFrequency];
    const governmentFees =
        selectedPaymentFrequency === 'onetime' ? [] : priceTaxData.annualTaxes ?? [];

    const coverables = useRecoilValue(allCoverablesAtom);
    const privateContent: PrivateContentDTO | undefined = coverables.privateContent
        ? coverables.privateContent[0]
        : undefined;
    const youthContent: YouthContentDTO | undefined = coverables.youthContent
        ? coverables.youthContent[0]
        : undefined;
    const contentAddressDawaId = privateContent?.dawaId ?? youthContent?.dawaId;
    const filteredGovernmentFees = filterGovernmentFees(governmentFees);

    const externalCasesData = useRecoilValue(externalCasesAtom);
    const { isPolicyChange } = useRecoilValue(jobInfoAtom);
    const { awaitBoundJob } = useAwaitBoundJob(false);
    const { issueInsurance } = useIssueInsurance();
    const checkoutAccordions = getCheckoutConfig(
        coverables,
        externalCasesData,
        isPolicyChange ?? false
    ).filter((accordion) => !accordion.disabled);
    const [isLoading, setIsLoading] = useState(false);
    const { setQuoteApprovedTimestamp } = useSetQuoteApprovedTimestampHook();
    const accountData = useRecoilValue(accountHolderAtom);
    const accountAddressDawaId = accountData.dawaId;

    // Prefetch data for CheckoutPage
    useAsyncRecoilValue(insuranceCompaniesAtom);
    useAsyncRecoilValue(fetchedPaymentInformationAtom);

    const getContinueButtonText = (): string => {
        if (businessFlow === 'DA') {
            return jobData.jobStatus === ACCEPTED
                ? getHippoText('DSS-SUMMARY-BUTTON-ACCEPTED')
                : getHippoText('DSS-SUMMARY-BUTTON-ACCEPT');
        }

        if (businessFlow === 'ONSA') {
            return getHippoText('DSS-SUMMARY-BUTTON');
        }

        if (businessFlow === 'OPC') {
            return getHippoText('DSS-SUMMARY-BUTTON-CONFIRM-CHANGES');
        }

        return '';
    };

    const onNextClick = (): void => {
        if (contentAddressDawaId && accountAddressDawaId !== contentAddressDawaId) {
            setShowModal(true);
            return;
        }
        void setQuoteApprovedTimestamp().then(() => {
            trackCheckout(sortedCoverables, wizardStep);
            setJobData({ ...jobData, jobStatus: ACCEPTED });
            if (checkoutAccordions.length === 0) {
                setIsLoading(true);
                void issueInsurance()
                    .then(awaitBoundJob)
                    .then(() => {
                        wizardGotoStep('ReceiptPage');
                    });
            } else {
                wizardNextStep();
            }
        });
    };

    if (isLoading) {
        return <LoadingDots />;
    }

    return (
        <WizardPagetemplate
            hidePrevious
            hideNext
            onNext={wizardNextStep}
            onPrevious={wizardPreviousStep}
        >
            <Grid>
                <GridItem span={8} colStart={3}>
                    <ShoppingBasketSummaryHeader />
                    <Box>
                        <div id="shopping-basket-summary-container" className="u-spacing-inset-m">
                            <ShoppingBasketSummaryCoverableList
                                coverables={sortedCoverables ?? []}
                                paymentFrequencyPostfix={paymentFrequencyPostfix}
                                paymentFrequency={selectedPaymentFrequency}
                            />
                            <CustomerBenefit />
                            {!!filteredGovernmentFees.length && isPaymentYearly && (
                                <div className="u-spacing-inset-m--stretch-max">
                                    <ShoppingBasketGovernmentFee
                                        isPaymentYearly={isPaymentYearly}
                                        governmentFees={filteredGovernmentFees}
                                    />
                                </div>
                            )}
                            {businessFlow !== 'OPC' && !hasCoverableWithChangedStatus && (
                                <TotalPrice
                                    totalPrice={
                                        selectedPaymentFrequency === paymentFrequencyMap.monthly
                                            ? priceTaxData.monthlyPrice?.amount ?? 0
                                            : priceTaxData.yearlyPriceWithFees?.amount ?? 0
                                    }
                                    showYearlyPriceField={
                                        selectedPaymentFrequency === paymentFrequencyMap.monthly
                                    }
                                    paymentFrequencyPostfix={paymentFrequencyPostfix}
                                    displayTaxesSubtitle
                                    shouldShowPriceIncludingTaxes={
                                        selectedPaymentFrequency !== paymentFrequencyMap.monthly
                                    }
                                />
                            )}
                        </div>
                    </Box>
                    <div className="u-spacing-inset-m--stretch-max">
                        {!!filteredGovernmentFees.length && !isPaymentYearly && (
                            <Box>
                                <div className="u-spacing-inset-m--stretch-max">
                                    <ShoppingBasketGovernmentFee
                                        isPaymentYearly={isPaymentYearly}
                                        governmentFees={filteredGovernmentFees}
                                    />
                                </div>
                            </Box>
                        )}
                    </div>
                    <div className="u-spacing-inset-bottom-4 u-spacing-inset-bottom-none@xl">
                        <Button onClick={onNextClick}>{getContinueButtonText()}</Button>
                        <Button
                            onClick={() => {
                                wizardGotoStep('ShoppingBasketPage');
                            }}
                            theme="ghost"
                        >
                            Tilbage
                        </Button>
                    </div>
                </GridItem>
            </Grid>
            {showModal && (
                <CallMeModal
                    title={getHippoText('DSS-CALL-ME-MODAL-CONTENT-ADDRESS-HEADER')}
                    description={getHippoText('DSS-CALL-ME-MODAL-CONTENT-ADDRESS-SUBHEADER')}
                    activityCode="dss_call_request"
                    showModal={showModal}
                    closeModal={() => {
                        setShowModal(false);
                    }}
                />
            )}
        </WizardPagetemplate>
    );
};

export default ShoppingBasketSummaryPage;
